import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { AppContext } from '@state/context'
import {
  setCurrentSection,
  setSelectedSectionId,
} from '@state/actions/appActions'
import { openPanel } from '@state/actions/panelsActions'
import classNames from 'classnames'
import { storagePath } from '@helpers/variables'
import { SectionCardStyle } from './style'
import {
  CARD_SIZES,
  COLORS,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  PANELS,
  TEXT_SIZES,
  AVATAR_SIZE,
  USER_ROLES,
} from '@helpers/constants'
import Heading from '@components/Heading'
import Text from '@components/Text'
import Button from '@components/Button'
import Avatar from '@components/Avatar'
import Icon from '@components/Icon'
import { getSubjectTextClass } from '@helpers/subject'
import { useUpdateSection } from '@hooks/mutations/useUpdateSection'
import { ImageSize } from '@components/Avatar/style'
import { useConfirm } from '@hooks/useConfirm'
import withTooltip from '@components/WithTooltip'
import { useExamsAllowed } from '@hooks/useExamsAllowed'
import { anySectionHasQuizExams } from '@helpers/exams'
import { useOwnBooksAllowed } from '@hooks/useOwnBooksAllowed'

const ButtonWithTooltip = withTooltip(Button)

const SectionCard = (props) => {
  const { section, size = CARD_SIZES.LARGE, inLibraryView } = props

  const {
    id: sectionId,
    name: title,
    subject,
    level: { name: level },
    schoolYear,
    image,
    public: isPublic,
    published,
    isHidden,
    exams,
  } = section

  const [state, dispatch] = useContext(AppContext)

  const {
    app: { userId, userRole },
  } = state
  const container = classNames(SectionCardStyle.container)
  const buttonContainer = classNames(SectionCardStyle.button)
  const buttonTextContainer = classNames(SectionCardStyle.buttonText)
  const relativePosition = classNames('relative')
  const zindex = classNames('z-1 mb-4')
  const absolutePosition = classNames(SectionCardStyle.absolute)
  const isSaved = section?.customers.some((customer) => customer?.id === userId)
  const { post, isPosting } = useUpdateSection()
  const { confirm } = useConfirm()
  const ownBooksAllowed = useOwnBooksAllowed()

  const updateSection = async (
    connectCustomers = [],
    disconnectCustomers = []
  ) => {
    if (disconnectCustomers.length > 0) {
      const confirmed = await confirm({
        title: title,
        text: 'Är du säker på att du vill ta bort det här avsnittet från din bokhylla? Avsnittet försvinner INTE ur böcker som det används i.',
        confirmText: 'Ja, ta bort avsnitt',
        cancelText: 'Avbryt',
      })
      if (!confirmed) return
    }
    const res = await post(
      {
        id: sectionId,
        connectCustomers: connectCustomers,
        disconnectCustomers: disconnectCustomers,
      },
      connectCustomers.length > 0
        ? 'Sparad i bokhyllan.'
        : 'Borttagen från bokhyllan.',
      connectCustomers.length > 0
        ? 'Nåt gick fel när avsnittet skulle sparas i bokhyllan.'
        : 'Nåt gick fel när avsnittet skulle tas bort från bokhyllan.'
    )
  }

  const examsAllowed = useExamsAllowed()

  const hasQuizExams = anySectionHasQuizExams([section])

  return (
    <div className={container}>
      <div className='flex items-start justify-between'>
        {examsAllowed && hasQuizExams && (
          <div className='mb-3'>
            <Icon
              icon={'ExamsBoard'}
              iconClasses='w-[28px] h-[28px]'
              color={COLORS.SECONDARY_GREEN.TEXT}
            />
          </div>
        )}

        <div className={'flex items-center text-mediumGrey'}>
          {/* <Icon icon={'Classes'} /> */}
          {/* <Text
            size={TEXT_SIZES.XXXSMALL}
            color={COLORS.MEDIUM_GREY.TEXT}
            extraClasses={'pl-2 pr-2'}
            noPadding
          >
            {schoolYear.name}
          </Text> */}

          <Text
            size={TEXT_SIZES.XXSMALL}
            color={COLORS.MEDIUM_GREY.TEXT}
            noPadding
            extraClasses={'mb-2'}
          >
            {schoolYear.name} - Nivå: {level}
          </Text>
        </div>
      </div>

      <div className={buttonContainer}>
        <div className='flex flex-col'>
          <div className='my-2'>
            <div
              className={`flex items-center ${getSubjectTextClass(
                subject?.slug
              )}`}
            >
              <Icon icon={'Section'} />
              <Text
                color={getSubjectTextClass(subject?.slug)}
                noPadding
                extraClasses={'ml-2'}
                size={TEXT_SIZES.XXSMALL}
              >
                {subject?.name}
              </Text>
            </div>
            <div>
              <Heading
                size={TEXT_SIZES.LARGE}
                color={COLORS.WHITE.TEXT}
                headingLevel={HEADING_TYPE.H2}
              >
                {title}
              </Heading>
            </div>
          </div>
        </div>

        <div className={relativePosition}>
          {image && (
            <div className={zindex}>
              <Avatar
                size={AVATAR_SIZE.LARGE}
                url={`${storagePath}/${image}`}
              />
            </div>
          )}
          {!image && <div className={ImageSize.large} />}
          <div className={absolutePosition}>
            <ButtonWithTooltip
              size={BUTTON_SIZE.ICON_CIRCLE}
              icon={'Information'}
              type={BUTTON_TYPE.DARK_GREY}
              handleClick={() => {
                dispatch(setSelectedSectionId(sectionId))
                dispatch(openPanel(PANELS.VIEW_SECTION))
              }}
              tooltip={
                <span className='whitespace-nowrap'>
                  Information om avsnittet.
                </span>
              }
            />
          </div>
        </div>
      </div>
      <div className={buttonTextContainer}>
        <div className={'flex'}>
          <Button
            type={inLibraryView ? BUTTON_TYPE.DARK_GREY : BUTTON_TYPE.SUBJECT}
            subject={inLibraryView ? null : subject?.slug}
            to={`/avsnitt/${sectionId}`}
            marginRight
          >
            Öppna
          </Button>
          {ownBooksAllowed && (
            <>
              <ButtonWithTooltip
                type={BUTTON_TYPE.DARK_GREY}
                size={BUTTON_SIZE.MEDIUM}
                disabled={isPosting || (isSaved && inLibraryView)}
                handleClick={() => {
                  !isSaved
                    ? updateSection([userId], []) // connect
                    : updateSection([], [userId]) // disconnect
                }}
                marginRight
                icon={isSaved && !inLibraryView ? 'Exit' : 'Favourite'}
                iconRight
                tooltip={
                  isSaved
                    ? inLibraryView
                      ? 'Redan sparat i Min bokhylla.'
                      : 'Ta bort avsnittet från din bokhylla. Avsnittet försvinner INTE ur böcker som det används i.'
                    : 'Spara avsnittet i Min bokhylla.'
                }
              >
                {isSaved ? (inLibraryView ? 'Sparad' : 'Ta bort') : 'Spara'}
              </ButtonWithTooltip>
              <ButtonWithTooltip
                type={inLibraryView ? BUTTON_TYPE.SUBJECT : BUTTON_TYPE.DARK_GREY}
                subject={inLibraryView ? subject?.slug : null}
                icon={'Book'}
                iconRight
                handleClick={() => {
                  dispatch(setSelectedSectionId(sectionId))
                  dispatch(setCurrentSection(section))
                  dispatch(openPanel(PANELS.ADD_SECTION_TO_BOOK))
                }}
                tooltip={'Lägg till avsnittet i en egen bok.'}
              >
                Lägg till
              </ButtonWithTooltip>
            </>
          )}
        </div>
        {userRole === USER_ROLES.ADMIN && (
          <div>
            {!published && (
              <Text size={TEXT_SIZES.XXXSMALL} color={COLORS.ALERT_RED.TEXT}>
                UTKAST
              </Text>
            )}
            {isHidden && (
              <Text
                size={TEXT_SIZES.XXXSMALL}
                color={COLORS.SECONDARY_GREEN.TEXT}
              >
                GÖMD
              </Text>
            )}
            {isPublic && (
              <Text size={TEXT_SIZES.XXXSMALL} color={COLORS.ALERT_GREEN.TEXT}>
                PUBLIKT
              </Text>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

SectionCard.propTypes = {
  section: PropTypes.object,
  size: PropTypes.string,
  inLibraryView: PropTypes.bool,
}

export default SectionCard
